import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { CarrierNotes, RestService } from 'cargo-chief-commons';
import { EMPTY, Observable } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';

import { Cargochief } from '@service/cargochief/cargochief.service';
import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { EngageCarriersDialogComponent } from '../../project/engage-carriers-dialog/engage-carriers-dialog.component';

export interface LogCallContext {
	carrierId: string;
	loadId: string;
	bidId: string;
}

// CN-8646: a logged call yields both the contacted bid id (to flip the row) and the
// created note, so the caller can insert it into the notes list immediately rather
// than re-fetching (the note write briefly lags the list query — read-after-write).
export interface LogCallResult {
	bidId: string;
	note: CarrierNotes | null;
}

// CN-8616: shared "Log a call" flow. Centralises the dialog + note + mark-Contacted
// sequence so both the bid-list contact actions and the carrier contact-details
// modal behave identically (open the same EngageCarriers dialog Procurement
// Projects / carrier-outreach use, save a load + carrier note, then mark the bid
// Contacted).
@Injectable({ providedIn: 'root' })
export class LogCallService {
	constructor(
		private readonly dialog: MatDialog,
		private readonly restService: RestService,
		private readonly urlService: UrlService,
		private readonly utilService: UtilService,
		private readonly cargochief: Cargochief
	) {}

	// Opens the "Log a call" dialog. On save: persists a load + carrier note, then
	// marks the bid Contacted. Emits { bidId, note } on success; emits nothing if the
	// user cancels or a request fails (failures surface via a snackbar).
	openLogCall(ctx: LogCallContext): Observable<LogCallResult> {
		// A logged call must scope its note to the load + carrier and mark a specific
		// bid Contacted; bail out rather than open the dialog with missing IDs.
		if (!ctx?.bidId || !ctx?.carrierId || !ctx?.loadId) {
			return EMPTY;
		}

		const dialogRef = this.dialog.open(EngageCarriersDialogComponent, {
			width: '550px',
			panelClass: 'cargo_chief_dialog_container',
			data: {
				dialogHeader: 'Log a call',
				submitButtonText: 'Save',
				showRateForm: true,
				showNotes: true,
				carrierIds: [ctx.carrierId],
				contactNameOptional: false,
			},
		});

		// Note + mark-Contacted run as two sequential POSTs (this preserves the prior
		// inline Log Call behaviour). They aren't a single transaction: if the note
		// saves but mark-Contacted fails the user is told ("Call was logged but the bid
		// could not be marked as contacted") and can retry — there's no silent
		// inconsistency. A single transactional endpoint would be a backend follow-up.
		return dialogRef.afterClosed().pipe(
			filter((result) => !!result),
			switchMap((result) =>
				this.restService.POST(this.urlService.addCarrierNotes(), this.buildNote(ctx, result)).pipe(
					catchError(() => {
						this.utilService.showSnackbar(
							'Unable to save the call log. Try again.',
							SNACKBAR.ERROR,
							SNACKBAR.ACTION
						);

						return EMPTY;
					}),
					// CN-8646: keep the created note from the add response so the caller can
					// insert it into the list without depending on the (lagging) list query.
					switchMap((noteResponse) =>
						this.restService.POST(this.urlService.bidContactedUrl(), { id: ctx.bidId }).pipe(
							map(() => ({ bidId: ctx.bidId, note: noteResponse?.data ?? null })),
							catchError(() => {
								this.utilService.showSnackbar(
									'Call was logged but the bid could not be marked as contacted.',
									SNACKBAR.ERROR,
									SNACKBAR.ACTION
								);

								return EMPTY;
							})
						)
					)
				)
			)
		);
	}

	private buildNote(ctx: LogCallContext, result: any) {
		return {
			carrierId: ctx.carrierId,
			loadId: ctx.loadId,
			notes: this.composeCallNote(result),
			lastEditedBy: this.cargochief.getUser()?.firstName,
		};
	}

	// Mirror the carrier-outreach widget's note formatting so a logged call reads the
	// same wherever it was captured.
	private composeCallNote(result: any): string {
		const lines: string[] = [];

		if (result?.contactName) {
			lines.push(`Talked To: ${result.contactName}`);
		}
		const priceCents = Number(result?.desiredPrice);
		if (Number.isFinite(priceCents) && priceCents > 0) {
			lines.push(`Quoted: ${this.formatCents(priceCents)}`);
		}
		if (result?.status) {
			lines.push(`Status: ${this.humanizeStatus(result.status)}`);
		}
		if (result?.carrierRespondedAt) {
			lines.push(`Responded: ${result.carrierRespondedAt}`);
		}
		if (result?.notes) {
			lines.push(result.notes);
		}

		return lines.join('\n');
	}

	private formatCents(cents: number): string {
		return `$${(cents / 100).toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})}`;
	}

	private humanizeStatus(status: string): string {
		return status
			.toString()
			.toLowerCase()
			.split('_')
			.map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
			.join(' ');
	}
}
